import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Tabs, Button, Skeleton, Empty, List, Tag, Typography, Progress, Space } from 'antd';
import { BarChartOutlined, ReloadOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import MainLayout from '../../layouts/MainLayout';
import DashboardActivitiesCard from '../../components/dashboard/DashboardActivitiesCard';
import { dashboardApi } from '../../utils/api';
import { handleApiError } from '../../utils/errorHandler';

const { Text } = Typography;

const TAB_ITEMS = [
  { key: 'overview', label: 'نمای کلی' },
  { key: 'project', label: 'پروژه' },
  { key: 'client', label: 'مشتری' },
  { key: 'hr', label: 'منابع انسانی' },
  { key: 'ticket', label: 'تیکت' },
  { key: 'finance', label: 'مالی' },
];

const LABEL_MAP = {
  Draft: 'پیش‌نویس',
  'Not Sent': 'ارسال‌نشده',
  Unpaid: 'پرداخت‌نشده',
  Overdue: 'سررسید گذشته',
  'Partially Paid': 'پرداخت بخشی',
  Paid: 'پرداخت‌شده',
  Sent: 'ارسال‌شده',
  Declined: 'ردشده',
  Expired: 'منقضی',
  Accepted: 'پذیرفته‌شده',
  Waiting: 'در انتظار',
  Converted: 'تبدیل‌شده',
  Earnings: 'درآمد',
  'Hour(s)': 'ساعت',
  Hours: 'ساعت',
  Logged: 'ثبت‌شده',
  Generated: 'ایجادشده',
  'On going': 'در حال انجام',
  Win: 'موفق',
  Lost: 'ناموفق',
  Email: 'ایمیل',
  Phone: 'تلفن',
  Twitter: 'توییتر',
  Facebook: 'فیسبوک',
  Male: 'مرد',
  Female: 'زن',
  June: 'خرداد',
  July: 'تیر',
  August: 'مرداد',
  September: 'شهریور',
  October: 'مهر',
  November: 'آبان',
  December: 'آذر',
  January: 'دی',
  February: 'بهمن',
  March: 'اسفند',
  April: 'فروردین',
  May: 'اردیبهشت',
  finished: 'پایان‌یافته',
};

const toFaLabel = (label) => {
  if (label == null) return '—';
  const input = String(label).trim();
  return LABEL_MAP[input] || input;
};

const MetricGrid = ({ items = [] }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
    {items.map((it) => (
      <Card
        key={it.label}
        className="border-0 shadow-sm hover:shadow-lg transition-all bg-gradient-to-br from-white to-slate-50 dark:from-gray-800 dark:to-gray-800/80"
      >
        <div className="text-xs text-gray-500">{toFaLabel(it.label)}</div>
        <div className="mt-1 text-2xl font-extrabold text-gray-800 dark:text-gray-100 tracking-tight">{it.value}</div>
      </Card>
    ))}
  </div>
);

const LabelValueBars = ({ title, labels = [], values = [], colors = [] }) => (
  <Card title={title} className="border border-gray-100 dark:border-gray-700">
    {labels.length ? (
      <div className="space-y-3">
        {labels.map((label, i) => {
          const value = Number(values?.[i] || 0);
          const total = values.reduce((a, b) => Number(a) + Number(b || 0), 0) || 1;
          const percent = Math.min(100, Math.round((value / total) * 100));
          return (
            <div key={`${label}-${i}`}>
              <div className="mb-1 flex items-center justify-between text-xs">
                <span>{toFaLabel(label)}</span>
                <span>{value}</span>
              </div>
              <Progress
                percent={percent}
                size="small"
                showInfo={false}
                strokeColor={colors?.[i] || '#1677ff'}
              />
            </div>
          );
        })}
      </div>
    ) : <Empty description="داده‌ای برای نمودار وجود ندارد" />}
  </Card>
);

const TabSkeleton = ({ rows = 8 }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} className="border border-gray-100 dark:border-gray-700">
          <Skeleton active paragraph={{ rows: 1 }} title={{ width: '50%' }} />
        </Card>
      ))}
    </div>
    <Card className="border border-gray-100 dark:border-gray-700">
      <Skeleton active paragraph={{ rows }} />
    </Card>
  </div>
);

const EmptyState = ({ title }) => (
  <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center dark:border-gray-700 dark:bg-gray-900/40">
    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
      <BarChartOutlined className="text-gray-500" />
    </div>
    <div className="text-base font-semibold text-gray-700 dark:text-gray-200">{title}</div>
    <div className="mt-1 text-sm text-gray-500">برای بازه انتخاب‌شده داده‌ای ثبت نشده است.</div>
  </div>
);

const StatusPie = ({ title, labels = [], values = [], colors = [] }) => {
  const chartData = labels.map((label, i) => ({
    name: toFaLabel(label),
    value: Number(values?.[i] || 0),
    color: colors?.[i] || '#1677ff',
  }));
  const hasData = chartData.some((d) => d.value > 0);
  return (
    <Card title={title} className="border border-gray-100 dark:border-gray-700">
      {hasData ? (
        <div className="h-[320px] w-full" dir="ltr">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="45%"
                outerRadius={95}
                innerRadius={55}
                label={({ name, percent }) => `${name} ${Math.round(percent * 100)}٪`}
                labelLine={false}
              >
                {chartData.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [`${value}`, `${name}`]}
                contentStyle={{ borderRadius: 10, direction: 'rtl', fontFamily: 'CustomFont' }}
                labelStyle={{ direction: 'rtl' }}
              />
              <Legend
                formatter={(value) => <span style={{ fontFamily: 'CustomFont' }}>{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyState title="داده‌ای برای نمودار وجود ندارد" />
      )}
    </Card>
  );
};

const FancyListCard = ({ title, items = [], leftKey = 'label', rightKey = 'value' }) => (
  <Card title={title} className="border border-gray-100 dark:border-gray-700">
    {items.length ? (
      <List
        dataSource={items}
        renderItem={(item) => (
          <List.Item>
            <div className="flex w-full items-center justify-between gap-2">
              <span className="truncate">{toFaLabel(item[leftKey])}</span>
              <Tag color="blue">{item[rightKey]}</Tag>
            </div>
          </List.Item>
        )}
      />
    ) : <EmptyState title="داده‌ای برای نمایش وجود ندارد" />}
  </Card>
);

const mapPendingLeadFollowUps = (rows = []) =>
  rows.map((row) => {
    const f = row.followup || {};
    return {
      ...f,
      client_name: row.client_name,
      company_name: row.company_name,
      leads:
        f.lead_id || row.client_name
          ? {
              id: f.lead_id,
              client_name: row.client_name,
              company_name: row.company_name,
            }
          : null,
    };
  });

const AdvancedDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'overview';
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchTab = useCallback(async (t) => {
    setLoading(true);
    try {
      const res = await dashboardApi.getAdvanced(t);
      setData(res?.data || null);
    } catch (error) {
      setData(null);
      const tabFaMap = {
        overview: 'نمای کلی',
        project: 'پروژه',
        client: 'مشتری',
        hr: 'منابع انسانی',
        ticket: 'تیکت',
        finance: 'مالی',
      };
      handleApiError(
        error,
        `در دریافت اطلاعات تب «${tabFaMap[t] || 'داشبورد'}» خطا رخ داد.`,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTab(tab);
  }, [tab, fetchTab]);

  const content = useMemo(() => {
    if (!data) return <EmptyState title="داده‌ای موجود نیست" />;
    if (tab === 'overview') {
      const c = data.counts || {};
      const followUps = mapPendingLeadFollowUps(data.pendingLeadFollowUps);
      return (
        <div className="space-y-4">
          <MetricGrid items={[
            { label: 'کل مشتریان', value: c.totalClients ?? 0 },
            { label: 'کل کارمندان', value: c.totalEmployees ?? 0 },
            { label: 'کل پروژه‌ها', value: c.totalProjects ?? 0 },
            { label: 'فاکتور پرداخت‌نشده', value: c.totalUnpaidInvoices ?? 0 },
            { label: 'ساعات ثبت‌شده', value: c.totalHoursLogged ?? 0 },
            { label: 'تسک معلق', value: c.totalPendingTasks ?? 0 },
            { label: 'تیکت باز', value: c.totalOpenTickets ?? 0 },
            { label: 'تیکت حل‌شده', value: c.totalResolvedTickets ?? 0 },
            { label: 'حضور امروز', value: c.totalTodayAttendance ?? 0 },
          ]} />
          <DashboardActivitiesCard
            followUps={followUps}
            projectActivities={data.projectActivities || []}
            userActivities={data.userActivities || []}
          />
        </div>
      );
    }
    if (tab === 'project') {
      return (
        <div className="space-y-4">
          <MetricGrid items={[
            { label: 'کل پروژه‌ها', value: data.total_projects ?? 0 },
            { label: 'ساعت ثبت‌شده', value: data.total_hours_logged ?? 0 },
            { label: 'پروژه دارای تاخیر', value: data.total_overdue_projects ?? 0 },
          ]} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <StatusPie
              title="پروژه بر اساس وضعیت"
              labels={data.status_wise_project?.labels || []}
              values={data.status_wise_project?.values || []}
              colors={data.status_wise_project?.colors || []}
            />
            <FancyListCard
              title="مراحل در انتظار"
              items={(data.pending_milestones || []).map((m) => ({ label: m.title || m.name || 'مرحله', value: 'فعال' }))}
            />
          </div>
        </div>
      );
    }
    if (tab === 'client') {
      const s = data.stats || {};
      return (
        <div className="space-y-4">
          <MetricGrid items={[
            { label: 'کل مشتریان', value: s.total_client ?? 0 },
            { label: 'کل سرنخ', value: s.total_lead ?? 0 },
            { label: 'کل معاملات', value: s.total_deals ?? 0 },
            { label: 'تبدیل‌شده', value: s.converted_deals ?? 0 },
            { label: 'درصد تبدیل', value: `${s.convert_deal_percentage ?? 0}%` },
            { label: 'قرارداد صادرشده', value: s.total_contracts_generated ?? 0 },
            { label: 'قرارداد امضاشده', value: s.total_contracts_signed ?? 0 },
          ]} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <StatusPie title="سرنخ بر اساس وضعیت" labels={data.charts?.lead_status?.labels || []} values={data.charts?.lead_status?.values || []} colors={data.charts?.lead_status?.colors || []} />
            <StatusPie title="سرنخ بر اساس منبع" labels={data.charts?.lead_source?.labels || []} values={data.charts?.lead_source?.values || []} colors={data.charts?.lead_source?.colors || []} />
          </div>
        </div>
      );
    }
    if (tab === 'hr') {
      return (
        <div className="space-y-4">
          <MetricGrid items={[
            { label: 'کل کارمندان', value: data.total_employee ?? 0 },
            { label: 'مرخصی تاییدشده', value: data.total_leaves_approved ?? 0 },
            { label: 'ورود جدید', value: data.total_new_employee ?? 0 },
            { label: 'خروج کارمند', value: data.total_employee_exits ?? 0 },
            { label: 'میانگین حضور', value: `${data.average_attendance ?? 0}%` },
            { label: 'حضور امروز', value: data.counts?.totalTodayAttendance ?? 0 },
          ]} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <StatusPie title="توزیع بر اساس دپارتمان" labels={data.department_wise_chart?.labels || []} values={data.department_wise_chart?.values || []} colors={data.department_wise_chart?.colors || []} />
            <StatusPie title="توزیع بر اساس نقش" labels={data.role_wise_chart?.labels || []} values={data.role_wise_chart?.values || []} colors={data.role_wise_chart?.colors || []} />
          </div>
        </div>
      );
    }
    if (tab === 'ticket') {
      return (
        <div className="space-y-4">
          <MetricGrid items={[
            { label: 'حل‌نشده', value: data.counts?.totalUnresolvedTickets ?? 0 },
            { label: 'حل‌شده', value: data.counts?.totalResolvedTickets ?? 0 },
            { label: 'بی‌مسئول', value: data.counts?.totalUnassignedTicket ?? 0 },
          ]} />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <StatusPie title="بر اساس نوع" labels={data.charts?.type?.labels || []} values={data.charts?.type?.values || []} colors={data.charts?.type?.colors || []} />
            <StatusPie title="بر اساس وضعیت" labels={data.charts?.status?.labels || []} values={data.charts?.status?.values || []} colors={data.charts?.status?.colors || []} />
            <StatusPie title="بر اساس کانال" labels={data.charts?.channel?.labels || []} values={data.charts?.channel?.values || []} colors={data.charts?.channel?.colors || []} />
          </div>
        </div>
      );
    }
    if (tab === 'finance') {
      return (
        <div className="space-y-4">
          <MetricGrid items={[
            { label: 'پرداخت‌شده', value: data.totalPaidInvoice ?? 0 },
            { label: 'پرداخت‌نشده', value: data.totalUnPaidInvoice ?? 0 },
            { label: 'هزینه‌ها', value: data.totalExpenses ?? 0 },
            { label: 'درآمد', value: data.totalEarnings ?? 0 },
            { label: 'مبلغ معلق', value: data.totalPendingAmount ?? 0 },
          ]} />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <LabelValueBars title="نمای کلی فاکتورها" labels={data.invoiceOverviewChartData?.labels || []} values={data.invoiceOverviewChartData?.values || []} colors={data.invoiceOverviewChartData?.colors || []} />
            <LabelValueBars title="نمای کلی پیش‌فاکتورها" labels={data.estimateOverviewChartData?.labels || []} values={data.estimateOverviewChartData?.values || []} colors={data.estimateOverviewChartData?.colors || []} />
            <LabelValueBars title="نمای کلی پروپوزال‌ها" labels={data.proposalOverviewChartData?.labels || []} values={data.proposalOverviewChartData?.values || []} colors={data.proposalOverviewChartData?.colors || []} />
          </div>
        </div>
      );
    }
    return <EmptyState title="اطلاعاتی برای این تب یافت نشد" />;
  }, [tab, data]);

  return (
    <MainLayout>
      <div className="custom-font">
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-900/25 flex items-center justify-center">
                <BarChartOutlined className="text-indigo-600 dark:text-indigo-300 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">داشبورد پیشرفته</h1>
                <Text type="secondary">تحلیل وضعیت کسب‌وکار در تب‌های تخصصی</Text>
              </div>
            </div>
            <Space>
              <Button icon={<ReloadOutlined />} loading={loading} onClick={() => fetchTab(tab)}>بروزرسانی</Button>
            </Space>
          </div>
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl space-y-5">
            <Tabs
              type="card"
              activeKey={tab}
              onChange={(k) => setSearchParams({ tab: k })}
              items={TAB_ITEMS.map((t) => ({ key: t.key, label: t.label }))}
            />
            {loading ? <TabSkeleton rows={10} /> : content}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AdvancedDashboard;
